import { Modal, Select, Input, InputNumber, Button, Row, Col, Typography } from 'antd';
import {
  ScheduleOutlined, TagsOutlined, SafetyCertificateOutlined,
  NumberOutlined, CalendarOutlined, FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { API_URL, TOKEN } from '../../../variables/constants';
import { COLORS } from '../../../components/ui/colors';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AddFractionnement = ({ show, handleClose, editMode, selectedFractionnement, onSaved }) => {
  // State variables for form fields
  const [nom, setNom] = useState('');
  const [nombre, setNombre] = useState('');
  const [libelleFractionnement, setLibelleFractionnement] = useState('');
  const [description, setDescription] = useState('');
  const [moisMin, setMoisMin] = useState('');
  const [moisMax, setMoisMax] = useState('');
  const [categorie, setCategorie] = useState('');
  const [selectedGaranties, setSelectedGaranties] = useState([]); // Array of STRING codeIds for Ant Select
  const [loading, setLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]); // All categories
  const [garantiesList, setGarantiesList] = useState([]); // All guarantees
  const [catGaranties, setCatGaranties] = useState([]); // Guarantees filtered by selected category (for Select options)
  const [periode, setPeriode] = useState("MOIS");

  // --- Data Fetching ---
  // Fetch categories and all guarantees when the component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [categoriesRes, garantiesRes] = await Promise.all([
          axios.get(`${API_URL}/api/categorie`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
          axios.get(`${API_URL}/api/garantie`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
        ]);
        setCategoriesList(categoriesRes.data || []);
        setGarantiesList(garantiesRes.data || []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Erreur lors du chargement des données initiales");
      }
    };
    fetchInitialData();
  }, []); // Empty dependency array means this runs once on mount

  // --- Form Reset Logic ---
  // Use useCallback to memoize the reset function
  const resetForm = useCallback(() => {
    setNom('');
    setNombre('');
    setLibelleFractionnement('');
    setDescription('');
    setMoisMin('');
    setMoisMax('');
    setCategorie('');
    setSelectedGaranties([]); // Crucial to reset selected guarantees
    setCatGaranties([]); // And available guarantees
    setPeriode("MOIS");
  }, []);

  // --- Initialization for Edit Mode / Reset for Add Mode ---
  useEffect(() => {
    if (editMode && selectedFractionnement) {
      // Only proceed if both categoriesList and garantiesList are loaded
      if (categoriesList.length > 0 && garantiesList.length > 0) {
        const {
          nom,
          nombre,
          libelleFractionnement,
          description,
          moisMin,
          moisMax,
          categorie: selectedCatObject, // Renamed to avoid conflict with the state variable
          garanties,
          periode
        } = selectedFractionnement;

        // Set basic fields
        setNom(nom || '');
        setNombre(nombre || '');
        setLibelleFractionnement(libelleFractionnement || '');
        setDescription(description || '');
        setMoisMin(moisMin || '');
        setMoisMax(moisMax || '');
        setPeriode(periode || "MOIS");

        // Set category and filter available guarantees based on it
        const codeCat = selectedCatObject?.codeCategorie;
        setCategorie(codeCat || '');

        // Filter `garantiesList` to get `catGaranties` based on the selected category
        if (codeCat) {
          const filtered = garantiesList.filter(g => g.categorie?.codeCategorie === codeCat);
          setCatGaranties(filtered);
        } else {
          setCatGaranties([]);
        }

        // Set the selected guarantees for the Ant Design Select
        // It must be an array of STRINGS matching the `value` in `Select` options
        if (garanties && Array.isArray(garanties)) {
          const guaranteeCodeIds = garanties.map(gar => String(gar.codeId)).filter(Boolean);
          setSelectedGaranties(guaranteeCodeIds);
        } else {
          setSelectedGaranties([]);
        }
      }
    } else {
      // If not in edit mode (i.e., add mode), or if `show` changes, reset the form
      resetForm();
    }
  }, [editMode, selectedFractionnement, categoriesList, garantiesList, resetForm, show]); // Added `show` to dependencies

  // --- Dynamic Filtering of Guarantees ---
  // This useEffect ensures `catGaranties` is updated whenever `categorie` or `garantiesList` changes
  useEffect(() => {
    if (categorie && garantiesList.length > 0) {
      const filtered = garantiesList.filter(
        (gl) => gl.categorie?.codeCategorie === categorie
      );
      setCatGaranties(filtered);
    } else if (!categorie) {
      setCatGaranties([]); // Clear if no category selected
    }
  }, [categorie, garantiesList]);


  // --- Event Handlers ---
  const handleGarantiesChange = (value) => {
    // `value` from Ant Design Select is already an array of strings (the codeIds)
    setSelectedGaranties(value);
  };

  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Validation
    if (!libelleFractionnement?.trim()) { toast.warn("Le nom est requis"); return; }
    if (!categorie) { toast.warn("La catégorie est requise"); return; }
    if (!nombre) { toast.warn("Le nombre de versements est requis"); return; }
    if (moisMin === '' || moisMax === '') { toast.warn("Les bornes de période sont requises"); return; }

    setLoading(true);

    const selectedCategoryObject = categoriesList.find((c) => c.codeCategorie === categorie);

    // Prepare data for API call
    const fractionnementData = {
      nom,
      nombre: parseInt(nombre),
      libelleFractionnement,
      description,
      periode,
      moisMin: parseInt(moisMin),
      moisMax: parseInt(moisMax),
      categorie: selectedCategoryObject,
      // Map back the string IDs from `selectedGaranties` to full guarantee objects for the API
      garanties: selectedGaranties
        .map((gCodeId) => garantiesList.find((gar) => String(gar.codeId) === gCodeId))
        .filter(Boolean), // Filter out any undefined guarantees if not found
    };

    try {
      if (editMode) {
        // For update, include the existing ID from selectedFractionnement
        const updatedFractionnement = {
          ...selectedFractionnement,
          ...fractionnementData,
        };

        await axios.put(`${API_URL}/api/fractionnement`, updatedFractionnement, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        toast.success("Fractionnement modifié avec succès");
      } else {
        await axios.post(`${API_URL}/api/fractionnement`, fractionnementData, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        toast.success("Fractionnement ajouté avec succès");
      }

      onSaved(); // Notify parent component to refresh data and close modal
    } catch (err) {
      console.error("Erreur lors de l'opération :", err.response?.data || err.message);
      toast.error("Échec de l'opération");
    } finally {
      setLoading(false);
    }
  };

  // --- Render JSX ---
  const labelStyle = { display: "block", marginBottom: 6, fontWeight: 600, color: COLORS.secondary };

  return (
    <Modal
      open={show}
      onCancel={handleClose}
      footer={null}
      centered
      width={640}
      bodyStyle={{ padding: 0, borderRadius: 16, overflow: "hidden" }}
      style={{ borderRadius: 16, overflow: "hidden" }}
    >
      {/* En-tête dégradé */}
      <div style={{
        background: COLORS.gradient,
        padding: "24px 28px",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ScheduleOutlined style={{ fontSize: 24, color: "#fff" }} />
        </div>
        <div>
          <Title level={4} style={{ color: "#fff", margin: 0 }}>
            {editMode ? "Modifier le fractionnement" : "Nouveau fractionnement"}
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            Modalité de paiement et bornes de période par catégorie
          </Text>
        </div>
      </div>

      <div style={{ padding: "24px 28px" }}>
        {/* Nom */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Nom du fractionnement</label>
          <Input
            size="large"
            prefix={<ScheduleOutlined style={{ color: "#bbb" }} />}
            placeholder="Ex : Aucun, Deux versements…"
            value={libelleFractionnement}
            onChange={(e) => setLibelleFractionnement(e.target.value)}
          />
        </div>

        {/* Catégorie + Nombre */}
        <Row gutter={16}>
          <Col xs={24} sm={12} style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Catégorie</label>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Sélectionner"
              value={categorie || undefined}
              suffixIcon={<TagsOutlined />}
              onChange={(val) => { setCategorie(val); setSelectedGaranties([]); }}
              showSearch
              optionFilterProp="label"
              options={categoriesList.map((cat) => ({ label: `${cat.nom} (${cat.codeCategorie})`, value: cat.codeCategorie }))}
            />
          </Col>
          <Col xs={24} sm={12} style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Nombre de versements</label>
            <InputNumber
              size="large"
              style={{ width: "100%" }}
              min={1}
              prefix={<NumberOutlined style={{ color: "#bbb" }} />}
              placeholder="1"
              value={nombre === '' ? null : Number(nombre)}
              onChange={(v) => setNombre(v ?? '')}
            />
          </Col>
        </Row>

        {/* Garanties */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}><SafetyCertificateOutlined style={{ marginRight: 6 }} />Garanties</label>
          <Select
            mode="multiple"
            size="large"
            style={{ width: "100%" }}
            placeholder={categorie ? "Sélectionner les garanties" : "Choisissez d'abord une catégorie"}
            value={selectedGaranties}
            onChange={handleGarantiesChange}
            options={catGaranties.map((g) => ({ label: g.nom, value: String(g.codeId) }))}
            disabled={!categorie}
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        {/* Période + bornes */}
        <Row gutter={16}>
          <Col xs={24} sm={8} style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Période</label>
            <Select
              size="large"
              style={{ width: "100%" }}
              value={periode}
              suffixIcon={<CalendarOutlined />}
              onChange={(val) => setPeriode(val)}
              options={[
                { label: "Mois", value: "MOIS" },
                { label: "Jour", value: "JOUR" },
                { label: "An", value: "AN" },
              ]}
            />
          </Col>
          <Col xs={12} sm={8} style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Min ({periode.toLowerCase()})</label>
            <InputNumber
              size="large"
              style={{ width: "100%" }}
              min={0}
              placeholder="Min"
              value={moisMin === '' ? null : Number(moisMin)}
              onChange={(v) => setMoisMin(v ?? '')}
            />
          </Col>
          <Col xs={12} sm={8} style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Max ({periode.toLowerCase()})</label>
            <InputNumber
              size="large"
              style={{ width: "100%" }}
              min={0}
              placeholder="Max"
              value={moisMax === '' ? null : Number(moisMax)}
              onChange={(v) => setMoisMax(v ?? '')}
            />
          </Col>
        </Row>

        {/* Description */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}><FileTextOutlined style={{ marginRight: 6 }} />Description</label>
          <TextArea
            rows={3}
            placeholder="Description du fractionnement"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Boutons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button size="large" onClick={handleClose} style={{ borderRadius: 8 }}>
            Annuler
          </Button>
          <Button
            size="large"
            type="primary"
            loading={loading}
            onClick={() => onSubmit()}
            style={{ borderRadius: 8, background: COLORS.gradient, border: "none", fontWeight: 600, minWidth: 140 }}
          >
            {editMode ? "Modifier" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddFractionnement;